/**
 * SF-4758: weights (admin:priorities:insights). A voter's points are multiplied by their person
 * weight if set, else their team's weight, else 1. Saved with PUT /settings/weights, which merges:
 * only what is sent changes, and a person weight of null clears it.
 */
import { useEffect, useRef } from 'react';
import { App, Button, Card, Col, Form, InputNumber, Row, Select, Space } from 'antd';
import { TEAM_KEYS, TEAM_LABELS, type TeamKey } from '../../../utils/teams';
import {
  prioritiesErrorMessage,
  setWeightsArgs,
  usePrioritiesWrite,
  type Participant,
  type PrioritiesSettings,
  type WeightsInput,
} from '../api/prioritiesClient';
import { WEIGHT_MAX, WEIGHT_MIN, WEIGHT_STEP } from '../constants';

/** The range rule, checked here so a bad number never leaves the page (sf-api checks again). */
const weightRules = [
  { required: true, message: 'Enter a weight' },
  {
    type: 'number' as const,
    min: WEIGHT_MIN,
    max: WEIGHT_MAX,
    message: `Between ${WEIGHT_MIN} and ${WEIGHT_MAX}`,
  },
];

function useSaveWeights() {
  const { message } = App.useApp();
  const write = usePrioritiesWrite();
  /** True when the weights were saved; false (with the error shown) when they were not. */
  const save = async (values: WeightsInput, done: string) => {
    try {
      await write.run(setWeightsArgs(values));
      void message.success(done);
      return true;
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'The weights could not be saved.'));
      return false;
    }
  };
  return { save, isLoading: write.isLoading };
}

export const TeamWeightsForm: React.FC<{ settings: PrioritiesSettings | undefined }> = ({
  settings,
}) => {
  const [form] = Form.useForm<Record<TeamKey, number>>();
  const { save, isLoading } = useSaveWeights();
  // Edits not yet saved. Every priorities write refreshes the settings; a refresh must not
  // overwrite team weights someone is still typing, so it only fills the form while it is clean.
  const edits = useRef(0);
  const savedEdits = useRef(0);

  useEffect(() => {
    if (settings?.teamWeights && edits.current === savedEdits.current) {
      form.setFieldsValue(settings.teamWeights);
    }
  }, [settings, form]);

  const submit = async (teamWeights: Record<TeamKey, number>) => {
    const sent = edits.current;
    // Clean again only if nothing was typed while the save was on its way.
    if (await save({ teamWeights }, 'Team weights saved')) savedEdits.current = sent;
  };

  return (
    <Card size="small" title="Team weights">
      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => {
          edits.current += 1;
        }}
        onFinish={submit}
      >
        <Row gutter={16}>
          {TEAM_KEYS.map((team) => (
            <Col key={team} xs={12} md={6}>
              <Form.Item name={team} label={TEAM_LABELS[team]} rules={weightRules}>
                <InputNumber step={WEIGHT_STEP} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          ))}
        </Row>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          Save team weights
        </Button>
      </Form>
    </Card>
  );
};

interface PersonWeightValues {
  email: string;
  weight: number;
}

export const PersonWeightForm: React.FC<{ people: Participant[] }> = ({ people }) => {
  const [form] = Form.useForm<PersonWeightValues>();
  const { save, isLoading } = useSaveWeights();
  const email = Form.useWatch('email', form);
  const chosen = people.find((p) => p.email === email);

  const clear = async () => {
    if (!email) return;
    // A refused or failed clear leaves the weight in place, so the field keeps showing it.
    if (await save({ personWeights: [{ email, weight: null }] }, 'Person weight cleared')) {
      form.resetFields(['weight']);
    }
  };

  return (
    <Card size="small" title="Person weight (overrides the team weight)">
      <Form
        form={form}
        layout="inline"
        onFinish={({ email: who, weight }) =>
          save({ personWeights: [{ email: who, weight }] }, 'Person weight saved')
        }
      >
        <Form.Item name="email" rules={[{ required: true, message: 'Pick a person' }]}>
          <Select
            aria-label="Person"
            placeholder="Person"
            style={{ minWidth: 240 }}
            showSearch
            optionFilterProp="label"
            options={people.map((p) => ({ value: p.email, label: p.name || p.email }))}
          />
        </Form.Item>
        <Form.Item name="weight" rules={weightRules}>
          <InputNumber aria-label="Weight" step={WEIGHT_STEP} placeholder="Weight" />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Set weight
          </Button>
          <Button onClick={clear} disabled={chosen?.weightSource !== 'person'}>
            Clear person weight
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
